/**
 * DatePicker — A custom date picker with three tabbed panels.
 *
 * Dependencies: react, styled-components, motion/react (framer-motion v12+),
 *               Font Awesome 6 (fas fa-chevron-down, fa-chevron-left, fa-chevron-right,
 *               fa-caret-up, fa-caret-down)
 *
 * Props:
 *   value    (string, required) — Selected date as "YYYY-MM-DD". If empty/null, defaults view to today.
 *   onChange  (fn, required)    — Called with new "YYYY-MM-DD" string when a day is picked.
 *   onClose   (fn, optional)   — Called after a day is picked (use to close the dropdown).
 *   minDate  (string, optional) — Earliest selectable date as "YYYY-MM-DD". Disables all earlier
 *                                  days, months, and year decrement beyond this bound.
 *   maxDate  (string, optional) — Latest selectable date as "YYYY-MM-DD". Same behavior as minDate
 *                                  but for the upper bound.
 *
 * Layout:
 *   - Top nav bar (dark background) with 3 tabs: Month | Day | Year.
 *     Clicking a tab reveals its panel with an animated slide-down.
 *   - Day tab (default): Calendar grid for the viewed month. Clicking a day fires onChange + onClose.
 *     Prev/next month links at the bottom. Today is highlighted in cream; selected day in blue.
 *   - Month tab: 3×4 grid of abbreviated month names. Clicking one sets the viewed month and
 *     switches to the day tab.
 *   - Year tab: Large year display with up/down caret buttons to increment/decrement.
 *
 * Positioning / Portaling:
 *   This component renders the picker panel inline — it does NOT portal itself.
 *   If used inside a scroll container with overflow clipping, the PARENT is responsible
 *   for portaling. Typical pattern:
 *
 *     import { createPortal } from "react-dom";
 *
 *     // Calculate position from trigger element's getBoundingClientRect()
 *     {createPortal(
 *       <div style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 10000 }}>
 *         <DatePicker value={date} onChange={setDate} onClose={() => setOpen(false)} />
 *       </div>,
 *       document.body
 *     )}
 *
 *   Remember to add click-outside detection and reposition on scroll/resize.
 *
 * Customization:
 *   - NAV_BG: Nav bar background color (dark slate).
 *   - SEL_BLUE: Selected day/month highlight color.
 *   - Adjust these constants at the top of the file to theme the picker.
 *   - Width is fixed at 340px via the Wrap styled component.
 */

import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import styled, { css } from "styled-components";
import { AnimatePresence, motion } from "motion/react";

/* Tab order for directional animation: month=0, day=1, year=2.
   Going to a higher index = push down (exit up, enter from bottom).
   Going to a lower index  = push up (exit down, enter from top). */
const TAB_INDEX = { month: 0, day: 1, year: 2 };

const panelVariants = {
  enter: (dir) => ({ y: dir > 0 ? "100%" : "-100%", opacity: 0 }),
  center: { y: 0, opacity: 1 },
  exit: (dir) => ({ y: dir > 0 ? "-100%" : "100%", opacity: 0 }),
};

const panelTransition = { type: "spring", stiffness: 350, damping: 26 };

const monthVariants = {
  enter: (dir) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? "-100%" : "100%", opacity: 0 }),
};

const MONTHS_ABBREV = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTHS_FULL = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTHS_GRID = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];

const NAV_BG = "#3b4d5e";
const SEL_BLUE = "#4a8fb8";

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
  return new Date(year, month, 1).getDay();
}

function parseDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  return { year: y, month: m - 1, day: d };
}

function toDateStr(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export default function DatePicker({ value, onChange, onClose, minDate, maxDate }) {
  const parsed = parseDate(value);
  const now = new Date();

  const [tab, setTabRaw] = useState("day");
  const dirRef = useRef(0);
  const [panelTransitioning, setPanelTransitioning] = useState(false);
  const setTab = useCallback((next) => {
    setTabRaw((prev) => {
      dirRef.current = TAB_INDEX[next] > TAB_INDEX[prev] ? 1 : -1;
      return next;
    });
    setPanelTransitioning(true);
  }, []);
  const [viewMonth, setViewMonth] = useState(parsed?.month ?? now.getMonth());
  const [viewYear, setViewYear] = useState(parsed?.year ?? now.getFullYear());
  const monthDirRef = useRef(0);

  /* ---- measure month tab width ---- */
  const monthMeasureRef = useRef(null);
  const [monthTabWidth, setMonthTabWidth] = useState(null);
  const monthTabFirst = useRef(true);

  useLayoutEffect(() => {
    if (monthMeasureRef.current) {
      setMonthTabWidth(monthMeasureRef.current.offsetWidth);
    }
    if (monthTabFirst.current) monthTabFirst.current = false;
  }, [viewMonth]);

  const min = parseDate(minDate);
  const max = parseDate(maxDate);

  const selDay = parsed?.day ?? null;
  const selMonth = parsed?.month ?? null;
  const selYear = parsed?.year ?? null;

  /* ---- clamp a day into the valid range for a given month/year ---- */
  const clampDay = (day, month, year) => {
    const maxDays = getDaysInMonth(year, month);
    let d = Math.min(day, maxDays);
    if (min) {
      const minD = new Date(min.year, min.month, min.day);
      if (new Date(year, month, d) < minD) {
        d = (year === min.year && month === min.month) ? min.day : 1;
      }
    }
    if (max) {
      const maxD = new Date(max.year, max.month, max.day);
      if (new Date(year, month, d) > maxD) {
        d = (year === max.year && month === max.month) ? max.day : maxDays;
      }
    }
    return d;
  };

  /* ---- constraints ---- */
  const isDayDisabled = (day) => {
    const d = new Date(viewYear, viewMonth, day);
    if (min && d < new Date(min.year, min.month, min.day)) return true;
    if (max && d > new Date(max.year, max.month, max.day)) return true;
    return false;
  };

  const isMonthDisabled = (mi) => {
    if (min && (viewYear < min.year || (viewYear === min.year && mi < min.month))) return true;
    if (max && (viewYear > max.year || (viewYear === max.year && mi > max.month))) return true;
    return false;
  };

  const canYearUp = !max || viewYear < max.year;
  const canYearDown = !min || viewYear > min.year;

  /* ---- next/prev month ---- */
  const nextMi = (viewMonth + 1) % 12;
  const nextYr = viewMonth === 11 ? viewYear + 1 : viewYear;
  const canNext = !max || nextYr < max.year || (nextYr === max.year && nextMi <= max.month);

  const prevMi = viewMonth === 0 ? 11 : viewMonth - 1;
  const prevYr = viewMonth === 0 ? viewYear - 1 : viewYear;
  const canPrev = !min || prevYr > min.year || (prevYr === min.year && prevMi >= min.month);

  /* ---- handlers ---- */
  const pickDay = (day) => {
    if (isDayDisabled(day)) return;
    onChange(toDateStr(viewYear, viewMonth, day));
    setTimeout(() => onClose?.(), 250);
  };

  const pickMonth = (mi) => {
    if (isMonthDisabled(mi)) return;
    setViewMonth(mi);
    // Carry the selected day forward, clamped to new month
    const day = clampDay(selDay ?? 1, mi, viewYear);
    onChange(toDateStr(viewYear, mi, day));
    setTab("day");
  };

  const [yearInput, setYearInput] = useState(String(viewYear));
  const yearInputRef = useRef(null);

  // Keep yearInput in sync when viewYear changes externally (arrows, etc.)
  useEffect(() => { setYearInput(String(viewYear)); }, [viewYear]);

  const applyYear = (newYear) => {
    setViewYear(newYear);
    const month = clampMonth(viewMonth, newYear);
    if (month !== viewMonth) setViewMonth(month);
    const day = clampDay(selDay ?? 1, month, newYear);
    onChange(toDateStr(newYear, month, day));
  };

  const clampMonth = (m, yr) => {
    if (min && yr === min.year && m < min.month) return min.month;
    if (max && yr === max.year && m > max.month) return max.month;
    return m;
  };

  const commitYear = () => {
    const n = parseInt(yearInput, 10);
    if (!isNaN(n) && n >= 1900 && n <= 2100) {
      let clamped = n;
      if (min) clamped = Math.max(clamped, min.year);
      if (max) clamped = Math.min(clamped, max.year);
      applyYear(clamped);
      setYearInput(String(clamped));
    } else {
      setYearInput(String(viewYear));
    }
  };

  const yearUp = () => {
    if (canYearUp) applyYear(viewYear + 1);
  };
  const yearDown = () => {
    if (canYearDown) applyYear(viewYear - 1);
  };

  const goNext = () => {
    if (!canNext) return;
    monthDirRef.current = 1;
    const nm = viewMonth === 11 ? 0 : viewMonth + 1;
    const ny = viewMonth === 11 ? viewYear + 1 : viewYear;
    setViewMonth(nm);
    if (viewMonth === 11) setViewYear((y) => y + 1);
    const day = clampDay(selDay ?? 1, nm, ny);
    onChange(toDateStr(ny, nm, day));
  };

  const goPrev = () => {
    if (!canPrev) return;
    monthDirRef.current = -1;
    const pm = viewMonth === 0 ? 11 : viewMonth - 1;
    const py = viewMonth === 0 ? viewYear - 1 : viewYear;
    setViewMonth(pm);
    if (viewMonth === 0) setViewYear((y) => y - 1);
    const day = clampDay(selDay ?? 1, pm, py);
    onChange(toDateStr(py, pm, day));
  };

  // Clamp viewMonth when year changes near min/max
  useEffect(() => {
    if (min && viewYear === min.year && viewMonth < min.month) setViewMonth(min.month);
    if (max && viewYear === max.year && viewMonth > max.month) setViewMonth(max.month);
  }, [viewYear]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- calendar grid ---- */
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDow = getFirstDayOfWeek(viewYear, viewMonth);
  const weeks = [];
  let week = new Array(firstDow).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    week.push(d);
    if (week.length === 7) { weeks.push(week); week = []; }
  }
  if (week.length) { while (week.length < 7) week.push(null); weeks.push(week); }

  const todayD = now.getDate();
  const todayM = now.getMonth();
  const todayY = now.getFullYear();
  const isViewToday = viewMonth === todayM && viewYear === todayY;
  const isViewSel = viewMonth === selMonth && viewYear === selYear;

  /* ---- animate panel + calendar heights ---- */
  // CalCell=36px, CalRow border-bottom=1px (except last) → row=37px, last=36px
  const calHeight = weeks.length * 36 + 3;

  const innerRef = useRef(null);
  const [clipHeight, setClipHeight] = useState(0);
  const initialRender = useRef(true);

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (innerRef.current) setClipHeight(innerRef.current.offsetHeight);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Skip animation on first render
  useEffect(() => { initialRender.current = false; }, []);

  /* ---- render ---- */
  return (
    <Wrap>
      <NavBar>
        <MonthMeasure ref={monthMeasureRef} aria-hidden="true">
          {MONTHS_FULL[viewMonth]} <i className="fas fa-chevron-down" />
        </MonthMeasure>
        <NavTab
          $active={tab === "month"}
          onClick={() => setTab("month")}
          animate={monthTabWidth ? { width: monthTabWidth + 20 } : {}}
          transition={monthTabFirst.current ? { duration: 0 } : { type: "spring", stiffness: 400, damping: 30 }}
        >
          <span>{MONTHS_FULL[viewMonth]} <i className="fas fa-chevron-down" /></span>
        </NavTab>
        <NavTab $active={tab === "day"} onClick={() => setTab("day")}>
          <span>{isViewSel ? selDay : "–"} <i className="fas fa-chevron-down" /></span>
        </NavTab>
        <NavTab $active={tab === "year"} $pos="right" onClick={() => setTab("year")}>
          <span>{viewYear} <i className="fas fa-chevron-down" /></span>
        </NavTab>
      </NavBar>

      <PanelClip
        animate={{ height: clipHeight || "auto" }}
        transition={panelTransitioning && !initialRender.current ? panelTransition : { duration: 0 }}
        onAnimationComplete={() => setPanelTransitioning(false)}
      >
        <div ref={innerRef}>
        <AnimatePresence mode="popLayout" custom={dirRef.current} initial={false}>
          <PanelMotion
            key={tab}
            custom={dirRef.current}
            variants={panelVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={panelTransition}
          >
            {tab === "day" && (
              <DayPanel>
                <MonthClip
                  animate={{ height: calHeight }}
                  transition={initialRender.current ? { duration: 0 } : panelTransition}
                >
                  <AnimatePresence mode="popLayout" custom={monthDirRef.current} initial={false}>
                    <MonthSlide
                      key={`${viewYear}-${viewMonth}`}
                      custom={monthDirRef.current}
                      variants={monthVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={panelTransition}
                    >
                      <CalGrid>
                        {weeks.map((wk, wi) => (
                          <CalRow key={wi}>
                            {wk.map((d, di) => {
                              const isToday = isViewToday && d === todayD;
                              const isSel = isViewSel && d === selDay;
                              const disabled = d !== null && isDayDisabled(d);
                              const needsLeft = d !== null && di > 0 && wk[di - 1] === null;
                              const emptyAboveDate = d === null && wi === 0 && weeks.length > 1 && weeks[1][di] !== null;
                              const emptyBeforeDate = d === null && wi === 0 && di < 6 && wk[di + 1] !== null;
                              return (
                                <CalCell
                                  key={di}
                                  $selected={isSel}
                                  $today={isToday}
                                  $disabled={disabled}
                                  $empty={d === null}
                                  $needsLeft={needsLeft}
                                  $emptyAboveDate={emptyAboveDate}
                                  $emptyBeforeDate={emptyBeforeDate}

                                  onClick={d !== null && !disabled ? () => pickDay(d) : undefined}
                                >
                                  <span>{d}</span>
                                </CalCell>
                              );
                            })}
                          </CalRow>
                        ))}
                      </CalGrid>
                    </MonthSlide>
                  </AnimatePresence>
                </MonthClip>
                <MonthNavRow>
                  <MonthLink $side="left" $disabled={!canPrev} onClick={canPrev ? goPrev : undefined}>
                    <span><i className="fas fa-chevron-left" /> {MONTHS_FULL[prevMi]}</span>
                  </MonthLink>
                  <MonthLink $side="right" $disabled={!canNext} onClick={canNext ? goNext : undefined}>
                    <span>{MONTHS_FULL[nextMi]} <i className="fas fa-chevron-right" /></span>
                  </MonthLink>
                </MonthNavRow>
              </DayPanel>
            )}

            {tab === "month" && (
              <MonthGridWrap>
                {MONTHS_GRID.map((label, i) => (
                  <MonthCell
                    key={i}
                    $selected={i === viewMonth}
                    $disabled={isMonthDisabled(i)}
                    onClick={() => pickMonth(i)}
                  >
                    <span>{label}</span>
                  </MonthCell>
                ))}
              </MonthGridWrap>
            )}

            {tab === "year" && (
              <YearPanel>
                <YearArrow onClick={yearUp} $disabled={!canYearUp}>
                  <i className="fas fa-caret-up" />
                </YearArrow>
                <YearInput
                  ref={yearInputRef}
                  type="text"
                  inputMode="numeric"
                  value={yearInput}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
                    setYearInput(v);
                  }}
                  onBlur={commitYear}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); commitYear(); }
                    if (e.key === "ArrowUp") { e.preventDefault(); yearUp(); }
                    if (e.key === "ArrowDown") { e.preventDefault(); yearDown(); }
                  }}
                />
                <YearArrow onClick={yearDown} $disabled={!canYearDown}>
                  <i className="fas fa-caret-down" />
                </YearArrow>
              </YearPanel>
            )}
          </PanelMotion>
        </AnimatePresence>
        </div>
      </PanelClip>
    </Wrap>
  );
}

/* ======================== styles ======================== */

const Wrap = styled.div`
  width: 272px;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22), 0 2px 8px rgba(0, 0, 0, 0.1);
  background: ${NAV_BG};
  border: 2px solid ${NAV_BG};
`;

const NavBar = styled.div`
  display: flex;
  align-items: center;
  background: ${NAV_BG};
  padding: 4px 4px 6px;
  gap: 3px;
  position: relative;
`;

const MonthMeasure = styled.span`
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  white-space: nowrap;
  font-size: 17px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;

  i { font-size: 8px; }
`;

const NavTab = styled(motion.button)`
  appearance: none;
  border: none;
  font-family: inherit;
  font-size: 17px;
  font-weight: 600;
  padding: 6px 8px 6px 12px;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  overflow: hidden;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;

  ${(p) =>
    p.$active
      ? css`
          background: #fff;
          color: #374151;

          &:hover {
            background: #f0f0f0;
          }
        `
      : css`
          background: transparent;
          color: rgba(255, 255, 255, 0.75);

          &:hover {
            color: #fff;
            background: rgba(255, 255, 255, 0.1);
          }
        `}

  &:active > span {
    transform: scale(0.93);
  }

  ${(p) => p.$pos === "right" && "margin-left: auto;"}

  > span {
    display: flex;
    align-items: center;
    gap: 5px;
    transition: transform 0.1s;
  }

  i {
    font-size: 8px;
  }
`;

const PanelClip = styled(motion.div)`
  position: relative;
  overflow: hidden;
  border-radius: 12px 12px 0 0;
  background: #f5f5f5;
`;

const PanelMotion = styled(motion.div)``;

/* ---- Day Panel ---- */

const DayPanel = styled.div`
  padding: 0;
`;

const MonthClip = styled(motion.div)`
  position: relative;
  overflow: hidden;
`;

const MonthSlide = styled(motion.div)``;

const CalGrid = styled.div`
  display: flex;
  flex-direction: column;
`;

const CalRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);

  &:first-child > * {
    border-top: none;
  }
`;

const CalCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  font-size: 14px;
  font-weight: 500;
  cursor: ${(p) => (p.$empty || p.$disabled ? "default" : "pointer")};
  transition: background 0.12s;
  user-select: none;
  ${(p) => !p.$empty && css`border-right: 1px solid #e5e7eb;`}
  ${(p) => !p.$empty && css`border-bottom: 1px solid #e5e7eb;`}
  ${(p) => p.$emptyAboveDate && css`border-bottom: 1px solid #e5e7eb;`}
  ${(p) => p.$emptyBeforeDate && css`border-right: 1px solid #e5e7eb;`}

  &:first-child {
    border-left: none;
  }

  &:nth-child(7) {
    border-right: none;
  }

  > span {
    transition: transform 0.1s;
  }

  ${(p) =>
    p.$selected &&
    css`
      background: ${SEL_BLUE};
      color: #fff;
    `}

  ${(p) =>
    p.$today &&
    !p.$selected &&
    css`
      background: #fef3cd;
    `}

  ${(p) =>
    p.$disabled &&
    css`
      color: #ccc;
    `}

  ${(p) =>
    !p.$empty &&
    !p.$selected &&
    !p.$disabled &&
    css`
      &:hover {
        background: rgba(0, 0, 0, 0.06);
      }

      &:active > span {
        transform: scale(0.85);
      }
    `}
`;

const MonthNavRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1px 4px 4px;
`;

const MonthLink = styled.button`
  appearance: none;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(p) => p.$disabled ? "#ccc" : "#6b7280"};
  cursor: ${(p) => p.$disabled ? "default" : "pointer"};
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 3px ${(p) => p.$side === "left" ? "7px" : "5px"} 3px ${(p) => p.$side === "right" ? "7px" : "5px"};
  border-radius: 7px;
  transition: color 0.15s, background 0.15s;

  > span {
    display: flex;
    align-items: center;
    gap: 2px;
    transition: transform 0.1s;
  }

  ${(p) => !p.$disabled && css`
    &:hover {
      color: #374151;
      background: rgba(0, 0, 0, 0.05);
    }

    &:active {
      color: ${SEL_BLUE};
    }

    &:active > span {
      transform: scale(0.9);
    }
  `}

  i {
    font-size: 9px;
  }
`;

/* ---- Month Panel ---- */

const MonthGridWrap = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
`;

const MonthCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: ${(p) => (p.$disabled ? "default" : "pointer")};
  border-bottom: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  transition: background 0.12s;
  user-select: none;

  > span {
    transition: transform 0.1s;
  }

  &:nth-child(3n) {
    border-right: none;
  }

  &:nth-last-child(-n + 3) {
    border-bottom: none;
  }

  ${(p) =>
    p.$selected &&
    css`
      background: ${SEL_BLUE};
      color: #fff;
    `}

  ${(p) =>
    p.$disabled &&
    css`
      color: #ccc;
    `}

  ${(p) =>
    !p.$selected &&
    !p.$disabled &&
    css`
      color: #4b5563;

      &:hover {
        background: rgba(0, 0, 0, 0.06);
      }

      &:active > span {
        transform: scale(0.85);
      }
    `}
`;

/* ---- Year Panel ---- */

const YearPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 0 10px;
  gap: 0;
`;

const YearArrow = styled.button`
  appearance: none;
  border: none;
  background: none;
  font-size: 32px;
  color: ${(p) => (p.$disabled ? "#ccc" : NAV_BG)};
  cursor: ${(p) => (p.$disabled ? "default" : "pointer")};
  padding: 0 14px;
  line-height: 1;
  transition: color 0.15s, transform 0.1s;

  ${(p) =>
    !p.$disabled &&
    css`
      &:hover {
        color: ${SEL_BLUE};
      }
      &:active {
        transform: scale(0.85);
      }
    `}
`;

const YearInput = styled.input`
  font-size: 28px;
  font-weight: 600;
  color: #1a1a1a;
  background: #fff;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  padding: 6px 0;
  width: calc(5ch + 12px);
  text-align: center;
  font-family: inherit;
  font-variant-numeric: tabular-nums;
  outline: none;
  transition: border-color 0.15s;

  &:focus {
    border-color: ${SEL_BLUE};
  }
`;
